'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import { CheckCircle2, Copy, KeyRound, Loader2, Rocket, ShieldCheck } from 'lucide-react';
import { useLandingLanguage } from '@/hooks/useLandingLanguage';
import { Card, CardTitle, ContentCard, MotionBlock } from '../shared';
type ProvisionState = 'idle' | 'creating' | 'done';
function buildDemoApiKey() {
  const seed = Math.random().toString(36).slice(2, 10).toUpperCase();
  return `ck_live_${seed}_ERV`;
}
export function PartnerKeyProvisionSimulation() {
  const $ = _c(123);
  const {
    language
  } = useLandingLanguage();
  const isEn = language === "en";
  const [partnerName, setPartnerName] = useState(isEn ? "Nexa ERP" : "Nexa ERP");
  const [contactEmail, setContactEmail] = useState(isEn ? "integration@nexaerp.com" : "integracija@nexaerp.rs");
  const [state, setState] = useState("idle");
  const [apiKey, setApiKey] = useState("");
  const [copied, setCopied] = useState(false);
  let t0;
  let t1;
  if ($[0] !== state) {
    t0 = () => {
      if (state !== "creating") {
        return;
      }
      const timer = window.setTimeout(() => {
        setApiKey(buildDemoApiKey());
        setState("done");
      }, 2200);
      return () => window.clearTimeout(timer);
    };
    t1 = [state];
    $[0] = state;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== isEn) {
    t2 = isEn ? {
      eyebrow: "Interactive Demo",
      title: "How partner API key provisioning looks in ERV",
      subtitle: "Simulate the same flow partners use: create integration client and receive key in seconds.",
      partnerLabel: "Partner name",
      emailLabel: "Contact email",
      cta: "Generate key",
      creating: "Generating credentials...",
      outputTitle: "Provisioning output",
      speedLabel: "Time to key",
      speedValue: "~2s in demo",
      speedHint: "Production follows same flow with environment validation.",
      statusIdle: "Ready to create client",
      statusCreating: "Creating integration client...",
      statusDone: "Credentials generated in a few seconds",
      keyLabel: "API key",
      nextStepLabel: "Next step",
      nextStepValue: "Enable webhook or test endpoint in sandbox.",
      copy: "Copy",
      copied: "Copied",
      windowTitle: "Cadrino ERV - integrations demo",
      notes: ["Client ID and key are generated instantly.", "Rate limits and expiration can be set immediately.", "Key is displayed once and then stored securely."]
    } : {
      eyebrow: "Interaktivni demo",
      title: "Kako izgleda izdavanje API klju\u010Da za partnere u ERV-u",
      subtitle: "Simulacija istog toka koji partneri koriste: kreiranje integracionog klijenta i klju\u010D za par sekundi.",
      partnerLabel: "Naziv partnera",
      emailLabel: "Kontakt email",
      cta: "Generi\u0161i klju\u010D",
      creating: "Generi\u0161em kredencijale...",
      outputTitle: "Izlaz provisioninga",
      speedLabel: "Vreme do klju\u010Da",
      speedValue: "~2s u demou",
      speedHint: "Produkcija prati isti tok uz validaciju okru\u017Eenja.",
      statusIdle: "Spremno za kreiranje klijenta",
      statusCreating: "Kreiram integracionog klijenta...",
      statusDone: "Kredencijali generisani za par sekundi",
      keyLabel: "API klju\u010D",
      nextStepLabel: "Slede\u0107i korak",
      nextStepValue: "Uklju\u010Dite webhook ili test endpoint u sandbox okru\u017Eenju.",
      copy: "Kopiraj",
      copied: "Kopirano",
      windowTitle: "Cadrino ERV - integracije demo",
      notes: ["Client ID i klju\u010D se kreiraju odmah.", "Rate limit i rok va\u017Eenja mogu da se podese odmah.", "Klju\u010D se prikazuje jednom i zatim \u010Duva bezbedno."]
    };
    $[3] = isEn;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const ui = t2;
  const statusText = state === "creating" ? ui.statusCreating : state === "done" ? ui.statusDone : ui.statusIdle;
  let t3;
  if ($[5] !== state) {
    t3 = () => {
      if (state === "creating") {
        return;
      }
      setCopied(false);
      setApiKey("");
      setState("creating");
    };
    $[5] = state;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const handleGenerate = t3;
  let t4;
  if ($[7] !== apiKey) {
    t4 = async () => {
      if (!apiKey) {
        return;
      }
      try {
        await navigator.clipboard.writeText(apiKey);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1800);
      } catch {
        setCopied(false);
      }
    };
    $[7] = apiKey;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleCopy = t4;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" aria-hidden={true} /><span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" aria-hidden={true} /><span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" aria-hidden={true} /></div>;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== ui.windowTitle) {
    t6 = <p className="text-xs font-medium tracking-wide text-slate-500">{ui.windowTitle}</p>;
    $[10] = ui.windowTitle;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <span className="w-[42px]" aria-hidden={true} />;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t6) {
    t8 = <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50/80 px-4 py-2.5">{t5}{t6}{t7}</div>;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== ui.eyebrow) {
    t9 = <p className="text-xs font-semibold uppercase tracking-wider text-cadrino-accent">{ui.eyebrow}</p>;
    $[15] = ui.eyebrow;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== ui.title) {
    t10 = <CardTitle className="mt-2">{ui.title}</CardTitle>;
    $[17] = ui.title;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== ui.subtitle) {
    t11 = <p className="mt-3 text-sm leading-relaxed text-cadrino-muted">{ui.subtitle}</p>;
    $[19] = ui.subtitle;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== ui.partnerLabel) {
    t12 = <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{ui.partnerLabel}</span>;
    $[21] = ui.partnerLabel;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  let t13;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = e => setPartnerName(e.target.value);
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== partnerName) {
    t14 = <input value={partnerName} onChange={t13} className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none ring-cadrino-primary/20 transition focus:ring-2" />;
    $[24] = partnerName;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t12 || $[27] !== t14) {
    t15 = <label className="space-y-1.5">{t12}{t14}</label>;
    $[26] = t12;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== ui.emailLabel) {
    t16 = <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{ui.emailLabel}</span>;
    $[29] = ui.emailLabel;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = e_0 => setContactEmail(e_0.target.value);
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  let t18;
  if ($[32] !== contactEmail) {
    t18 = <input value={contactEmail} onChange={t17} className="h-10 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm text-slate-800 outline-none ring-cadrino-primary/20 transition focus:ring-2" />;
    $[32] = contactEmail;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t16 || $[35] !== t18) {
    t19 = <label className="space-y-1.5">{t16}{t18}</label>;
    $[34] = t16;
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== t15 || $[38] !== t19) {
    t20 = <div className="mt-5 grid gap-3 sm:grid-cols-2">{t15}{t19}</div>;
    $[37] = t15;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== contactEmail || $[41] !== partnerName || $[42] !== state) {
    t21 = state === "creating" || !partnerName.trim() || !contactEmail.trim();
    $[40] = contactEmail;
    $[41] = partnerName;
    $[42] = state;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] !== state) {
    t22 = state === "creating" ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden={true} /> : <KeyRound className="h-4 w-4" aria-hidden={true} />;
    $[44] = state;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const t23 = state === "creating" ? ui.creating : ui.cta;
  let t24;
  if ($[46] !== t23) {
    t24 = <span>{t23}</span>;
    $[46] = t23;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== handleGenerate || $[49] !== t21 || $[50] !== t22 || $[51] !== t24) {
    t25 = <button type="button" onClick={handleGenerate} disabled={t21} className="mt-5 inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-cadrino-primary px-5 text-sm font-medium text-white transition-colors hover:bg-blue-600 disabled:cursor-not-allowed disabled:bg-blue-300">{t22}{t24}</button>;
    $[48] = handleGenerate;
    $[49] = t21;
    $[50] = t22;
    $[51] = t24;
    $[52] = t25;
  } else {
    t25 = $[52];
  }
  let t26;
  if ($[53] !== t10 || $[54] !== t11 || $[55] !== t20 || $[56] !== t25 || $[57] !== t9) {
    t26 = <MotionBlock><ContentCard className="p-6">{t9}{t10}{t11}{t20}{t25}</ContentCard></MotionBlock>;
    $[53] = t10;
    $[54] = t11;
    $[55] = t20;
    $[56] = t25;
    $[57] = t9;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== ui.outputTitle) {
    t27 = <p className="text-xs font-semibold uppercase tracking-wider text-cadrino-primary">{ui.outputTitle}</p>;
    $[59] = ui.outputTitle;
    $[60] = t27;
  } else {
    t27 = $[60];
  }
  let t28;
  if ($[61] !== ui.speedLabel) {
    t28 = <p className="text-[11px] font-semibold uppercase tracking-wide text-cadrino-primary">{ui.speedLabel}</p>;
    $[61] = ui.speedLabel;
    $[62] = t28;
  } else {
    t28 = $[62];
  }
  let t29;
  if ($[63] !== ui.speedValue) {
    t29 = <p className="mt-0.5 text-sm font-semibold text-slate-900">{ui.speedValue}</p>;
    $[63] = ui.speedValue;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== ui.speedHint) {
    t30 = <p className="mt-0.5 text-xs text-slate-600">{ui.speedHint}</p>;
    $[65] = ui.speedHint;
    $[66] = t30;
  } else {
    t30 = $[66];
  }
  let t31;
  if ($[67] !== t28 || $[68] !== t29 || $[69] !== t30) {
    t31 = <div className="mt-3 rounded-lg border border-blue-200/80 bg-blue-50/70 px-3 py-2">{t28}{t29}{t30}</div>;
    $[67] = t28;
    $[68] = t29;
    $[69] = t30;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t32 = <p className="text-xs uppercase tracking-wide text-slate-500">status</p>;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== state) {
    t33 = state === "creating" ? <Loader2 className="h-4 w-4 animate-spin text-cadrino-primary" aria-hidden={true} /> : <ShieldCheck className="h-4 w-4 text-emerald-600" aria-hidden={true} />;
    $[72] = state;
    $[73] = t33;
  } else {
    t33 = $[73];
  }
  let t34;
  if ($[74] !== statusText) {
    t34 = <span>{statusText}</span>;
    $[74] = statusText;
    $[75] = t34;
  } else {
    t34 = $[75];
  }
  let t35;
  if ($[76] !== t33 || $[77] !== t34) {
    t35 = <div className="mt-1 flex items-center gap-2 text-sm font-medium text-slate-800">{t33}{t34}</div>;
    $[76] = t33;
    $[77] = t34;
    $[78] = t35;
  } else {
    t35 = $[78];
  }
  let t36;
  if ($[79] !== ui.keyLabel) {
    t36 = <p className="text-xs uppercase tracking-wide text-slate-500">{ui.keyLabel}</p>;
    $[79] = ui.keyLabel;
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  const t37 = state === "done" ? apiKey : "ck_live_****************";
  let t38;
  if ($[81] !== t37) {
    t38 = <div className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-700">{t37}</div>;
    $[81] = t37;
    $[82] = t38;
  } else {
    t38 = $[82];
  }
  let t39;
  if ($[83] !== handleCopy) {
    t39 = () => void handleCopy();
    $[83] = handleCopy;
    $[84] = t39;
  } else {
    t39 = $[84];
  }
  const t40 = state !== "done";
  let t41;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <Copy className="h-3.5 w-3.5" aria-hidden={true} />;
    $[85] = t41;
  } else {
    t41 = $[85];
  }
  const t42 = copied ? ui.copied : ui.copy;
  let t43;
  if ($[86] !== t39 || $[87] !== t40 || $[88] !== t42) {
    t43 = <button type="button" onClick={t39} disabled={t40} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50">{t41}{t42}</button>;
    $[86] = t39;
    $[87] = t40;
    $[88] = t42;
    $[89] = t43;
  } else {
    t43 = $[89];
  }
  let t44;
  if ($[90] !== t38 || $[91] !== t43) {
    t44 = <div className="mt-1 flex items-center gap-2">{t38}{t43}</div>;
    $[90] = t38;
    $[91] = t43;
    $[92] = t44;
  } else {
    t44 = $[92];
  }
  let t45;
  if ($[93] !== t36 || $[94] !== t44) {
    t45 = <div className="mt-4">{t36}{t44}</div>;
    $[93] = t36;
    $[94] = t44;
    $[95] = t45;
  } else {
    t45 = $[95];
  }
  let t46;
  if ($[96] !== t35 || $[97] !== t45) {
    t46 = <div className="mt-4 rounded-xl border border-slate-200 bg-white p-4">{t32}{t35}{t45}</div>;
    $[96] = t35;
    $[97] = t45;
    $[98] = t46;
  } else {
    t46 = $[98];
  }
  let t47;
  if ($[99] !== ui.nextStepLabel) {
    t47 = <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{ui.nextStepLabel}</p>;
    $[99] = ui.nextStepLabel;
    $[100] = t47;
  } else {
    t47 = $[100];
  }
  let t48;
  if ($[101] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Rocket className="mt-0.5 h-4 w-4 shrink-0 text-cadrino-primary" aria-hidden={true} />;
    $[101] = t48;
  } else {
    t48 = $[101];
  }
  let t49;
  if ($[102] !== ui.nextStepValue) {
    t49 = <p className="mt-1 flex items-start gap-2 text-sm text-slate-700">{t48}<span>{ui.nextStepValue}</span></p>;
    $[102] = ui.nextStepValue;
    $[103] = t49;
  } else {
    t49 = $[103];
  }
  let t50;
  if ($[104] !== t47 || $[105] !== t49) {
    t50 = <div className="mt-3 rounded-lg border border-slate-200 bg-white/90 px-3 py-2.5">{t47}{t49}</div>;
    $[104] = t47;
    $[105] = t49;
    $[106] = t50;
  } else {
    t50 = $[106];
  }
  let t51;
  if ($[107] !== ui.notes) {
    t51 = ui.notes.map(_temp);
    $[107] = ui.notes;
    $[108] = t51;
  } else {
    t51 = $[108];
  }
  let t52;
  if ($[109] !== t51) {
    t52 = <ul className="mt-4 space-y-2.5 text-sm text-cadrino-muted">{t51}</ul>;
    $[109] = t51;
    $[110] = t52;
  } else {
    t52 = $[110];
  }
  let t53;
  if ($[111] !== t27 || $[112] !== t31 || $[113] !== t46 || $[114] !== t50 || $[115] !== t52) {
    t53 = <MotionBlock delay={0.04}><Card className="h-full border-cadrino-primary/25 bg-gradient-to-b from-blue-50/80 to-white ring-1 ring-cadrino-primary/10">{t27}{t31}{t46}{t50}{t52}</Card></MotionBlock>;
    $[111] = t27;
    $[112] = t31;
    $[113] = t46;
    $[114] = t50;
    $[115] = t52;
    $[116] = t53;
  } else {
    t53 = $[116];
  }
  let t54;
  if ($[117] !== t26 || $[118] !== t53) {
    t54 = <div className="grid gap-4 bg-gradient-to-b from-slate-50/50 to-white p-4 lg:grid-cols-[1.08fr_0.92fr]">{t26}{t53}</div>;
    $[117] = t26;
    $[118] = t53;
    $[119] = t54;
  } else {
    t54 = $[119];
  }
  let t55;
  if ($[120] !== t54 || $[121] !== t8) {
    t55 = <div className="mt-10 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">{t8}{t54}</div>;
    $[120] = t54;
    $[121] = t8;
    $[122] = t55;
  } else {
    t55 = $[122];
  }
  return t55;
}
function _temp(note) {
  return <li key={note} className="flex gap-2"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cadrino-primary" aria-hidden={true} /><span>{note}</span></li>;
}